import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Edit3, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import type {
  RequisitoLegalAspecto,
  RequisitoLegalFilters,
  RequisitoLegalRow,
} from "../types/requisitos-legales";

type Props = {
  rows: RequisitoLegalRow[];
  aspectos: RequisitoLegalAspecto[];
  loading: boolean;
  error: string | null;
  filters: RequisitoLegalFilters;
  onFiltersChange: (patch: Partial<RequisitoLegalFilters>) => void;
  onResetFilters: () => void;
  onRetry: () => void;
  onCreate: () => void;
  onEdit: (row: RequisitoLegalRow) => void;
  onDelete: (row: RequisitoLegalRow) => void;
};

type ReferenciaResumen = {
  referenciaId: number;
  referencia: string;
  total: number;
};

export function RequisitosLegalesTable({
  rows,
  aspectos,
  loading,
  error,
  filters,
  onFiltersChange,
  onResetFilters,
  onRetry,
  onCreate,
  onEdit,
  onDelete,
}: Props) {
  const [selectedReferenciaId, setSelectedReferenciaId] = useState<number | null>(null);
  const [referencesCollapsed, setReferencesCollapsed] = useState(false);

  const referencias = useMemo<ReferenciaResumen[]>(() => {
    const grouped = new Map<number, ReferenciaResumen>();

    rows.forEach((row) => {
      const current = grouped.get(row.referencia_id);

      if (current) {
        current.total += 1;
        return;
      }

      grouped.set(row.referencia_id, {
        referenciaId: row.referencia_id,
        referencia: row.referencia?.trim() || "Sin referencia",
        total: 1,
      });
    });

    return Array.from(grouped.values()).sort((a, b) =>
      a.referencia.localeCompare(b.referencia, "es", { numeric: true }),
    );
  }, [rows]);

  useEffect(() => {
    if (
      selectedReferenciaId !== null &&
      !referencias.some((item) => item.referenciaId === selectedReferenciaId)
    ) {
      setSelectedReferenciaId(null);
    }
  }, [referencias, selectedReferenciaId]);

  const visibleRows = useMemo(
    () =>
      selectedReferenciaId === null
        ? rows
        : rows.filter((row) => row.referencia_id === selectedReferenciaId),
    [rows, selectedReferenciaId],
  );

  const selectedReferencia =
    selectedReferenciaId === null
      ? null
      : referencias.find((item) => item.referenciaId === selectedReferenciaId) ?? null;

  return (
    <section className="reqleg-panel reqleg-main-panel">
      <div className="reqleg-panel-toolbar">
        <div>
          <div className="calidad-toolbar-eyebrow">Matriz normativa</div>
          <h3 className="calidad-toolbar-title">Requisitos legales</h3>
          <p className="calidad-toolbar-copy">
            Selecciona una referencia para consultar todos sus requisitos asociados.
          </p>
        </div>

        <div className="calidad-toolbar-meta">
          <span className="calidad-toolbar-count">
            {loading ? "Cargando..." : `${visibleRows.length} registros visibles`}
          </span>
          <button type="button" onClick={onCreate} className="calidad-primary-button">
            <Plus size={16} />
            Nuevo registro
          </button>
        </div>
      </div>

      <div className="reqleg-filters">
        <label className="reqleg-filter-field reqleg-filter-search">
          <span>Buscar</span>
          <input
            value={filters.search}
            onChange={(event) => onFiltersChange({ search: event.target.value })}
            placeholder="Aspecto, referencia, requisito, registro..."
          />
        </label>

        <label className="reqleg-filter-field">
          <span>Aspecto</span>
          <select
            value={filters.aspecto_id}
            onChange={(event) => onFiltersChange({ aspecto_id: event.target.value })}
          >
            <option value="">Todos</option>
            {aspectos.map((aspecto) => (
              <option key={aspecto.id} value={String(aspecto.id)}>
                {aspecto.nombre}
              </option>
            ))}
          </select>
        </label>

        <label className="reqleg-filter-field">
          <span>Ámbito</span>
          <select
            value={filters.ambito}
            onChange={(event) => onFiltersChange({ ambito: event.target.value })}
          >
            <option value="">Todos</option>
            <option value="europea">Europea</option>
            <option value="estatal">Estatal</option>
            <option value="autonomica">Autonómica</option>
            <option value="local">Local</option>
            <option value="otro">Otro</option>
          </select>
        </label>

        <label className="reqleg-filter-field">
          <span>Derogada</span>
          <select
            value={filters.derogada}
            onChange={(event) =>
              onFiltersChange({ derogada: event.target.value as RequisitoLegalFilters["derogada"] })
            }
          >
            <option value="">Todas</option>
            <option value="si">Sí</option>
            <option value="no">No</option>
          </select>
        </label>

        <label className="reqleg-filter-field">
          <span>Cumplimiento</span>
          <select
            value={filters.cumplimiento}
            onChange={(event) =>
              onFiltersChange({
                cumplimiento: event.target.value as RequisitoLegalFilters["cumplimiento"],
              })
            }
          >
            <option value="">Todos</option>
            <option value="si">Sí</option>
            <option value="no">No</option>
            <option value="pendiente">Pendiente</option>
          </select>
        </label>

        <button type="button" onClick={onResetFilters} className="calidad-toolbar-reset">
          Limpiar filtros
        </button>
      </div>

      {error ? (
        <div className="calidad-feedback-state">
          <div className="calidad-feedback-title">No se pudo cargar la matriz</div>
          <p className="calidad-feedback-copy">{error}</p>
          <button type="button" onClick={onRetry} className="calidad-feedback-action">
            <RefreshCw size={15} />
            Reintentar
          </button>
        </div>
      ) : loading ? (
        <div className="calidad-feedback-state">
          <Loader2 size={20} className="animate-spin text-[#0b4a2f]" />
          <div className="calidad-feedback-title">Cargando requisitos legales</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="calidad-feedback-state">
          <div className="calidad-feedback-title">No hay registros con los filtros actuales</div>
          <p className="calidad-feedback-copy">
            Crea un nuevo requisito legal o limpia los filtros.
          </p>
          <button type="button" onClick={onCreate} className="calidad-primary-button">
            <Plus size={16} />
            Nuevo registro
          </button>
        </div>
      ) : (
        <div
          className={`reqleg-reference-layout ${
            referencesCollapsed ? "references-collapsed" : ""
          }`}
        >
          <aside
            className={`reqleg-reference-list ${referencesCollapsed ? "collapsed" : ""}`}
            aria-label="Referencias legales"
          >
            <div className="reqleg-reference-list-header">
              <span className="reqleg-reference-list-title">Referencias</span>
              <button
                type="button"
                className="reqleg-reference-toggle"
                onClick={() => setReferencesCollapsed((current) => !current)}
                title={
                  referencesCollapsed
                    ? "Ampliar listado de referencias"
                    : "Reducir listado de referencias"
                }
                aria-label={
                  referencesCollapsed
                    ? "Ampliar listado de referencias"
                    : "Reducir listado de referencias"
                }
                aria-expanded={!referencesCollapsed}
              >
                {referencesCollapsed ? <ArrowRight size={17} /> : <ArrowLeft size={17} />}
              </button>
            </div>

            <button
              type="button"
              className={`reqleg-reference-card ${selectedReferenciaId === null ? "active" : ""}`}
              onClick={() => setSelectedReferenciaId(null)}
              title="Todas las referencias"
            >
              <span>Todas las referencias</span>
              <strong>{rows.length}</strong>
            </button>

            {referencias.map((item) => (
              <button
                key={item.referenciaId}
                type="button"
                className={`reqleg-reference-card ${
                  selectedReferenciaId === item.referenciaId ? "active" : ""
                }`}
                onClick={() => setSelectedReferenciaId(item.referenciaId)}
                title={item.referencia}
              >
                <span>{item.referencia}</span>
                <strong>{item.total}</strong>
              </button>
            ))}
          </aside>

          <div className="reqleg-reference-content">
            <div className="reqleg-selected-reference">
              <span>Referencia seleccionada</span>
              <strong>{selectedReferencia?.referencia ?? "Todas las referencias"}</strong>
            </div>

            <div className="reqleg-table-scroll">
              <table className="calidad-table reqleg-table reqleg-main-table min-w-full">
                <thead>
                  <tr>
                    <th className="reqleg-actions-col">Acciones</th>
                    <th>Aspecto</th>
                    <th>Referencia</th>
                    <th>Ámbito</th>
                    <th>Derogada</th>
                    <th>Requisito</th>
                    <th>Periodicidad</th>
                    <th>Registro a obtener</th>
                    <th>Cumplimiento</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="reqleg-empty-reference-cell">
                        No hay requisitos para la referencia seleccionada con los filtros actuales.
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((row) => (
                      <tr key={row.requisito_id}>
                        <td className="reqleg-actions-col">
                          <div className="calidad-table-actions-row">
                            <button
                              type="button"
                              onClick={() => onEdit(row)}
                              className="calidad-icon-button"
                              title="Editar requisito legal"
                              aria-label="Editar requisito legal"
                            >
                              <Edit3 size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => onDelete(row)}
                              className="calidad-icon-button danger"
                              title="Eliminar requisito legal"
                              aria-label="Eliminar requisito legal"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                        <td className="reqleg-aspecto-col">{row.aspecto}</td>
                        <td className="reqleg-referencia-col">
                          {renderReferenciaText(row.referencia)}
                        </td>
                        <td>{formatAmbito(row.ambito, row.ambito_original)}</td>
                        <td>{row.derogada ? "Sí" : "No"}</td>
                        <td className="reqleg-long-col">{renderRequisitoText(row.requisito)}</td>
                        <td className="reqleg-medium-col">{renderText(row.periodicidad)}</td>
                        <td className="reqleg-medium-col">
                          {renderRegistroText(row.registro_a_obtener)}
                        </td>
                        <td>{formatCumplimiento(row.cumplimiento)}</td>
                        <td className="reqleg-medium-col">
                          {renderObservacionesText(row.observaciones)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function renderReferenciaText(value: string | null) {
  if (!value?.trim()) {
    return <span className="calidad-muted-copy">Sin contenido</span>;
  }

  return (
    <div className="reqleg-reference-scroll-cell" title={value}>
      {value}
    </div>
  );
}

function renderRequisitoText(value: string | null) {
  if (!value?.trim()) {
    return <span className="calidad-muted-copy">Sin contenido</span>;
  }

  return (
    <div className="reqleg-requirement-scroll-cell" title={value}>
      {value}
    </div>
  );
}

function renderRegistroText(value: string | null) {
  if (!value?.trim()) {
    return <span className="calidad-muted-copy">Sin contenido</span>;
  }

  return (
    <div className="reqleg-secondary-scroll-cell" title={value}>
      {value}
    </div>
  );
}

function renderObservacionesText(value: string | null) {
  if (!value?.trim()) {
    return <span className="calidad-muted-copy">Sin contenido</span>;
  }

  return (
    <div className="reqleg-secondary-scroll-cell" title={value}>
      {value}
    </div>
  );
}

function renderText(value: string | null) {
  if (!value?.trim()) {
    return <span className="calidad-muted-copy">Sin contenido</span>;
  }

  return <div className="reqleg-text-cell">{value}</div>;
}

function formatAmbito(value: string, original: string | null) {
  if (value === "otro" && original) {
    return original;
  }

  const labels: Record<string, string> = {
    europea: "Europea",
    estatal: "Estatal",
    autonomica: "Autonómica",
    local: "Local",
    otro: "Otro",
  };

  return labels[value] ?? value;
}

function formatCumplimiento(value: boolean | null) {
  if (value === null) {
    return "Pendiente";
  }

  return value ? "Sí" : "No";
}
